import { useTranslation } from 'react-i18next'
import { LocalizedLink } from '@/components/LocalizedLink'
import { ProductMedia } from './ProductMedia'
import type { ContentLocale } from '@/data/public-catalog'

export function EmotraceBody() {
  const { t } = useTranslation('common')
  return <section className="shell-container product-section product-emotrace-body" aria-labelledby="bedside-title">
    <div className="product-copy">
      <h2 id="bedside-title">{t('productPage.emotrace.bodyTitle')}</h2>
      <p>{t('productPage.emotrace.intro')}</p>
      <p>{t('productPage.emotrace.checking')}</p>
      <ul className="product-matching">
        {['patient', 'sample', 'bag'].map(key => <li key={key}><h3>{t(`productPage.emotrace.${key}Title`)}</h3><p>{t(`productPage.emotrace.${key}`)}</p></li>)}
      </ul>
    </div>
    <ProductMedia path="/media/source/images/emotrace-scaled-e1775747461105.png" altKey="productPage.emotrace.deviceAlt" className="product-handheld" />
  </section>
}

export function DcBody({ language }: { language: ContentLocale }) {
  const { t } = useTranslation('common')
  const operations = t('productPage.dc.operations', { returnObjects: true }) as string[]
  const specs = t('productPage.dc.specs', { returnObjects: true }) as string[]
  return <section className="shell-container product-section product-dc-body" aria-labelledby="operation-title">
    <div className="product-copy"><h2 id="operation-title">{t('productPage.dc.bodyTitle')}</h2>
      {language === 'en' && <p className="catalog-source-status">{t('productPage.dc.sourceNote')}</p>}
      {language === 'it' ? operations.map((paragraph, index) => <p key={index}>{paragraph}</p>) : <ul className="product-operation-list">{operations.map((paragraph, index) => <li key={index}>{paragraph}</li>)}</ul>}
    </div>
    <aside className="product-specs" aria-labelledby="specs-title"><h2 id="specs-title">{t('productPage.dc.specsTitle')}</h2>
      <ul>{specs.map((spec, index) => <li key={index}>{spec}</li>)}</ul>
      <LocalizedLink className="product-source-link" to="https://endmed.com.tr/refleks-dc-2000-sl/" target="_blank" rel="noopener noreferrer">{t('productPage.manufacturerLink')}</LocalizedLink>
      {language === 'en' && <div className="product-video-reference"><p>{t('productPage.dc.videoNote')}</p><LocalizedLink to="https://www.youtube.com/watch?v=_jwx3RP_aFM" target="_blank" rel="noopener noreferrer">{t('productPage.dc.videoLink')}</LocalizedLink></div>}
    </aside>
  </section>
}

export function X12Body({ language }: { language: ContentLocale }) {
  const { t } = useTranslation('common')
  // The only textual source is Italian; the fallback is deliberate and labelled.
  const italian = (key: string) => t(`productPage.x12.${key}`, { lng: 'it' })
  return <div className="shell-container product-section product-x12-body">
    {language === 'en' && <p className="catalog-source-status product-fallback">{t('productPage.x12.fallback')}</p>}
    <div lang="it">
      <section className="product-copy" aria-labelledby="parameters-title"><h2 id="parameters-title">{italian('bodyTitle')}</h2><p>{italian('intro')}</p><p>{italian('parameters')}</p></section>
      <section className="product-analysis-steps" aria-labelledby="analysis-title"><h2 id="analysis-title">{italian('analysisTitle')}</h2><ol>
        <li><h3>{italian('preparationTitle')}</h3><p>{italian('preparation')}</p></li>
        <li><h3>{italian('readingTitle')}</h3><p>{italian('reading')}</p></li>
      </ol></section>
      <section className="product-copy" aria-labelledby="sampling-title"><h2 id="sampling-title">{italian('samplingTitle')}</h2><p>{italian('sampling')}</p></section>
    </div>
    <p className="catalog-source-status">{t('productPage.x12.mediaNote')}</p>
    <LocalizedLink className="product-source-link" to="https://usa.lenshooke.com/product.php?cat=30&id=198" target="_blank" rel="noopener noreferrer">{t('productPage.manufacturerLink')}</LocalizedLink>
  </div>
}
